import { ApolloClient } from "@apollo/client";

import {
  GET_LOCAL_SHOPPING_CART,
  HYDRATE_GUEST_SUBSCRIPTION_CART,
  PREPARE_GUEST_CART_TRANSFER,
  RESET_LOCAL_SHOPPING_CART,
} from "../graphql/resolvers/cart";
import { GET_MEMBER_CHECKOUT_INFO, GET_MEMBER_LAYOUT_INFO } from "../graphql/queries";
import {
  LocalShoppingCart,
  LocalShoppingCart_localShoppingCart as Cart,
  LocalShoppingCart_localShoppingCart_items as CartItem,
} from "../types/__generated__/LocalShoppingCart";
import { HydrateGuestSubscriptionCart } from "../types/__generated__/HydrateGuestSubscriptionCart";
import { PrepareGuestCartTransfer } from "../types/__generated__/PrepareGuestCartTransfer";
import { MemberLayoutInfo } from "../types/__generated__/MemberLayoutInfo";
import { MemberCheckoutInfo } from "../types/__generated__/MemberCheckoutInfo";
import { DB_ID_SUBSCRIPTION_STATUS_NEVER_ACTIVATED } from "./constants";
import { formatExtraInfoOnLocalShoppingCartSync } from "./tools";
import { sanitiseExtraInfoInput } from "./extraInfo";
import { calculateLocalCartProductsCost, calculateLocalCartShippingFee } from "./localCart";
import { clearGuestBoxIntent } from "./guestCheckout";
import {
  getGuestSubscriptionCheckout,
  GUEST_SUBSCRIPTION_TRANSFER_KEY,
  GuestBoxWineQuantity,
  GuestSubscriptionDestination,
  updateGuestSubscriptionCheckout,
} from "./guestSubscriptionCheckout";

const JOURNAL_KEY = GUEST_SUBSCRIPTION_TRANSFER_KEY;
type CartClient = Pick<ApolloClient<object>, "query" | "mutate" | "writeQuery">;

interface TransferJournal {
  countryId: number;
  memberId: number;
  cart: Cart;
  // Colour targets from the wine quantities step, snapshotted with the cart so every
  // replay and failed-row retry submits the same first-box request.
  boxWineQuantities?: GuestBoxWineQuantity[] | null;
  requestId: string | null;
  expiresAt?: string | null;
  unavailable?: boolean;
}

interface CartPricing {
  minBottlesForFreeShipping?: number | null;
  standardShippingFee?: number | null;
}

export interface GuestCartSyncResult {
  status: "empty" | "success" | "partial" | "error" | "expired";
  failedItems: CartItem[];
  errors: Array<{ messages?: string[] | null } | Error | null>;
}

export const clearGuestSubscriptionTransfer = () => window.localStorage.removeItem(JOURNAL_KEY);

const readJournal = (memberId: number, countryId: number): TransferJournal | null => {
  const stored = window.localStorage.getItem(JOURNAL_KEY);
  if (!stored) return null;
  const journal: TransferJournal = JSON.parse(stored);
  if (journal.requestId !== null && typeof journal.requestId !== "string") {
    throw new Error("Your saved cart request could not be read. Please review your saved items.");
  }
  if (journal.memberId !== memberId || journal.countryId !== countryId) {
    throw new Error("This saved cart belongs to another account. Please sign in with the original account.");
  }
  return journal;
};

const saveJournal = (journal: TransferJournal) =>
  window.localStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));

// Separate tabs share the journal, but not auth's in-memory promise map. Hold one
// origin-wide lock through the server response and durable checkpoint so they
// cannot submit different request IDs for the same saved items.
const withTransferLock = async <T>(callback: () => Promise<T>): Promise<T> => {
  const { locks } = window.navigator;
  return locks?.request ? locks.request(JOURNAL_KEY, callback) : callback();
};

const isDestination = (value: string | null | undefined): value is GuestSubscriptionDestination =>
  ["FIRST_SUBSCRIPTION", "EXISTING_SUBSCRIPTION", "ONE_OFF_CART"].includes(value || "");

const remainingCart = (cart: Cart, items: CartItem[], pricing: CartPricing): Cart => ({
  ...cart,
  visible: false,
  items,
  totalProductsCost: calculateLocalCartProductsCost(items),
  totalShippingFee:
    Number.isFinite(pricing.minBottlesForFreeShipping) && Number.isFinite(pricing.standardShippingFee)
      ? calculateLocalCartShippingFee(items, pricing.minBottlesForFreeShipping, pricing.standardShippingFee)
      : cart.totalShippingFee,
});

// The journal is saved synchronously before changing Apollo's debounced persisted cache. Reloads
// therefore restore only failed rows even when the preceding cache write was interrupted.
export const synchroniseGuestSubscriptionCart = async (
  client: CartClient,
  memberId: number,
  pricing: CartPricing = {}
): Promise<GuestCartSyncResult> => {
  let failedItems: CartItem[] = [];
  try {
    // Legacy JavaScript login callers can receive no member ID after a failed account query.
    // Do not overwrite the durable checkout with an invalid account binding in that case.
    if (!Number.isInteger(memberId) || memberId <= 0) {
      throw new Error("Your account couldn't be loaded. Please retry.");
    }
    return await withTransferLock<GuestCartSyncResult>(async () => {
      const { data } = await client.query<LocalShoppingCart>({
        query: GET_LOCAL_SHOPPING_CART,
        fetchPolicy: "cache-only",
      });
      // Also reread after the cache await in browsers without Web Locks: another
      // checkout may have bound the account or checkpointed rows while we waited.
      const checkout = getGuestSubscriptionCheckout();
      if (!checkout) throw new Error("Your saved checkout could not be found.");
      if (checkout.memberId && checkout.memberId !== memberId) {
        throw new Error("Please sign in with the account that started this checkout.");
      }
      updateGuestSubscriptionCheckout({ memberId });
      const journal = readJournal(memberId, checkout.countryId);
      const cart = journal?.cart || data?.localShoppingCart;
      if (!cart) throw new Error("Your saved cart could not be loaded. Please try again.");
      const boxWineQuantities = journal ? journal.boxWineQuantities ?? null : checkout.boxWineQuantities;
      const snapshot = (cart.items || []).filter((item): item is CartItem => !!item);
      failedItems = checkout.transferComplete ? [] : snapshot;

      if (!checkout.transferComplete) {
        if (journal?.unavailable) return { status: "expired", failedItems, errors: [] };
        let requestId = journal?.requestId ?? null;
        let expiresAt = journal?.expiresAt ?? null;
        saveJournal({
          memberId,
          countryId: checkout.countryId,
          cart,
          boxWineQuantities,
          requestId,
          expiresAt,
        });
        if (!requestId) {
          const preparation = await client.mutate<PrepareGuestCartTransfer>({
            mutation: PREPARE_GUEST_CART_TRANSFER,
            variables: { input: { memberId } },
            errorPolicy: "all",
          });
          const reserved = preparation.data?.prepareGuestCartTransfer;
          if (
            preparation.errors?.length ||
            reserved?.errors?.length ||
            !reserved?.requestId ||
            !reserved.expiresAt
          ) {
            throw new Error("We couldn't prepare your cart transfer. Please retry.");
          }
          requestId = reserved.requestId;
          expiresAt = reserved.expiresAt;
          // The ID must be durable before sending any item changes. A lost preparation
          // response can safely reserve again because no items have been submitted yet.
          saveJournal({
            memberId,
            countryId: checkout.countryId,
            cart,
            boxWineQuantities,
            requestId,
            expiresAt,
          });
        }
        const response = await client.mutate<HydrateGuestSubscriptionCart>({
          mutation: HYDRATE_GUEST_SUBSCRIPTION_CART,
          errorPolicy: "all",
          variables: {
            input: {
              memberId,
              subscriptionIntent: true,
              includeExistingSubscription: true,
              requestId,
              ...(boxWineQuantities?.length ? { firstBoxWineQuantities: boxWineQuantities } : {}),
              items: snapshot.map((item) => ({
                productId: item.product.id,
                quantity: item.quantity,
                redeemWithPoints: false,
                extraInfo:
                  sanitiseExtraInfoInput(formatExtraInfoOnLocalShoppingCartSync(item.extraInfo || {})) ||
                  null,
              })),
            },
          },
        });
        const payload = response.data?.bulkAddShoppingCartItems;
        if (
          payload?.errors?.some((error) =>
            ["GUEST_CART_TRANSFER_EXPIRED", "GUEST_CART_TRANSFER_NOT_FOUND"].includes(error?.code || "")
          )
        ) {
          saveJournal({
            memberId,
            countryId: checkout.countryId,
            cart,
            boxWineQuantities,
            requestId,
            expiresAt,
            unavailable: true,
          });
          return { status: "expired", failedItems, errors: payload.errors };
        }
        const results = payload?.itemResults || [];
        // Each submitted row must be represented exactly once. Field-name formatting is not a
        // transfer protocol: GraphQL middleware can camel-case those human-readable errors.
        const indexes = new Set(results.map((result) => result?.inputIndex));
        const completeResults =
          results.length === snapshot.length &&
          indexes.size === snapshot.length &&
          results.every((result) => result && result.inputIndex >= 0 && result.inputIndex < snapshot.length);
        if (!payload || !isDestination(payload.destination) || !completeResults) {
          throw new Error("We couldn't confirm the cart transfer. Please retry.");
        }
        failedItems = snapshot.filter(
          (_item, index) => !results.find((result) => result?.inputIndex === index)?.isSuccessful
        );
        const nextCart = remainingCart(cart, failedItems, pricing);
        saveJournal({
          memberId,
          countryId: checkout.countryId,
          cart: nextCart,
          boxWineQuantities,
          requestId: null,
        });
        updateGuestSubscriptionCheckout({
          destination: payload.destination,
          transferComplete: failedItems.length === 0,
        });
        client.writeQuery<LocalShoppingCart>({
          query: GET_LOCAL_SHOPPING_CART,
          data: { localShoppingCart: nextCart },
        });
        if (failedItems.length) {
          return {
            status: "partial",
            failedItems,
            errors: results.flatMap((result) => result?.errors || []).concat(payload.errors || []),
          };
        }
        // Even a GraphQL error accompanying a complete per-row payload cannot cause successful
        // items to be replayed. The checkpoint is already durable before any refetch below.
        if (response.errors?.length) throw new Error(response.errors[0].message);
      }

      await client.mutate({ mutation: RESET_LOCAL_SHOPPING_CART });
      clearGuestBoxIntent({ preserveCheckout: true });
      const refreshed = await client.query({ query: GET_MEMBER_CHECKOUT_INFO, fetchPolicy: "network-only" });
      if (refreshed.errors?.length) throw new Error(refreshed.errors[0].message);
      return { status: "success", failedItems: [], errors: [] };
    });
  } catch (error) {
    return {
      status: "error",
      failedItems,
      errors: [error instanceof Error ? error : new Error(String(error))],
    };
  }
};

export const discardGuestSubscriptionFailedItems = async (client: CartClient) =>
  withTransferLock(async () => {
    const checkout = getGuestSubscriptionCheckout();
    if (!checkout?.memberId) return;
    const journal = readJournal(checkout.memberId, checkout.countryId);
    if (journal) {
      saveJournal({
        ...journal,
        cart: remainingCart(journal.cart, [], {}),
        requestId: null,
        expiresAt: null,
      });
    }
    // A destination still needs resolving if the initial transfer failed before its response.
    updateGuestSubscriptionCheckout({ transferComplete: !!checkout.destination });
    await client.mutate({ mutation: RESET_LOCAL_SHOPPING_CART });
  });

export type GuestCartReviewDestination = "EXISTING_SUBSCRIPTION" | "CHECKOUT" | "WINES";

export const reviewGuestSubscriptionSavedItems = async (
  client: CartClient,
  memberId: number
): Promise<GuestCartReviewDestination> =>
  withTransferLock(async () => {
    const context = getGuestSubscriptionCheckout();
    if (!context || context.memberId !== memberId) throw new Error("Your account couldn't be confirmed.");
    const [layout, checkout] = await Promise.all([
      client.query<MemberLayoutInfo>({
        query: GET_MEMBER_LAYOUT_INFO,
        variables: { skipMe: false },
        fetchPolicy: "network-only",
        errorPolicy: "all",
      }),
      client.query<MemberCheckoutInfo>({
        query: GET_MEMBER_CHECKOUT_INFO,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      }),
    ]);
    const latest = getGuestSubscriptionCheckout();
    if (
      layout.errors?.length ||
      checkout.errors?.length ||
      layout.data?.me?.id !== memberId ||
      checkout.data?.me?.id !== memberId ||
      latest?.memberId !== memberId ||
      latest.countryId !== context.countryId
    ) {
      throw new Error("We couldn't load your saved items. Please try again.");
    }
    const { subscription } = layout.data.me;
    const previouslySubscribed =
      !!subscription &&
      (subscription.monthCount > 0 ||
        subscription.subscriptionStatus?.id !== DB_ID_SUBSCRIPTION_STATUS_NEVER_ACTIVATED);
    const hasCartItems = checkout.data.me.shoppingCart?.shoppingcartitemSet.some((item) => item.quantity > 0);
    await client.mutate({ mutation: RESET_LOCAL_SHOPPING_CART });
    clearGuestBoxIntent();
    if (previouslySubscribed) return "EXISTING_SUBSCRIPTION";
    return hasCartItems ? "CHECKOUT" : "WINES";
  });
